import { useState } from 'react';
import { Button, Group, Stack, Text } from '@mantine/core';
import { Popup } from 'react-map-gl/mapbox';
import { useSelectHandler } from '@lib/hooks';
import { Nuts } from '@src/types/maps';
import type { GeoJSONFeature } from 'mapbox-gl';

interface MapResultSelectProps {
  lat: number;
  lng: number;
  feature: GeoJSONFeature; // | undefined;
  setCompareState: React.Dispatch<React.SetStateAction<Nuts[]>>;
  onClose: () => void;
}

function MapResultSelect({ lat, lng, feature, setCompareState, onClose }: MapResultSelectProps) {
  const [selectClicked, setSelectClicked] = useState(false);
  const { selectHandler: selectProcessor } = useSelectHandler();

  if (!feature) return null;
  const { properties } = feature;
  const nuts: Nuts = {
    nutsId: properties?.nuts_id as string,
    nutsName: properties?.name_latn as string,
    countryName: properties?.country_name as string,
  };

  const compareHandler = ({ nutsId, nutsName, countryName }: Nuts) => {
    setCompareState((prev) => {
      const alreadyExists = prev.some((nut) => nut.nutsId === nutsId);
      if (alreadyExists) {
        return prev; // Return the previous state if the nutsId is already in the list
      }

      // only allow 3 nuts to compare, ignore if more
      if (prev.length < 3) {
        return [...prev, { nutsId, nutsName, countryName }];
      }
      return prev;
    });
    onClose();
  };

  const selectHandler = ({ nutsId, nutsName, countryName }: Nuts) => {
    setSelectClicked(true);
    selectProcessor({ nutsId, nutsName, countryName });
  };

  return (
    <Popup maxWidth="1200px" key={lat + lng} anchor="top" longitude={lng} latitude={lat} onClose={onClose}>
      <Stack m={10}>
        <Text fz="md" fw={700}>
          You Selected: {`${properties?.name_latn}, ${properties?.country_name}`}
        </Text>
        <Group gap={10} justify="center">
          <Button
            size="xs"
            variant="filled"
            color="green"
            disabled={selectClicked}
            onClick={() => selectHandler({ ...nuts })}
          >
            Select and Continue
          </Button>
          <Button size="xs" variant="filled" color="blue" onClick={() => compareHandler({ ...nuts })}>
            Add to Compare
          </Button>
        </Group>
      </Stack>
    </Popup>
  );
}

export default MapResultSelect;
